import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';

interface Props {
  godMode: boolean;
  setGodMode: (on: boolean) => void;
}

/**
 * Story 018 — toggle ON now requires explicit confirmation: god-mode
 * actions (manual pairing edits, voiding results, force-edits) bypass
 * the normal lifecycle guards and may make the tournament inadmissible
 * for FIDE rating. Toggle OFF is a one-click action — you can always
 * re-enable.
 *
 * Story 083 — there is now a single hub-wide toggle in the tournament
 * hub header; this component renders there.
 */
export function GodModeToggle({ godMode, setGodMode }: Props) {
  const { t } = useTranslation('common');
  const [confirming, setConfirming] = useState(false);

  function handleClick() {
    if (godMode) {
      setGodMode(false);
      return;
    }
    setConfirming(true);
  }

  function handleConfirm() {
    setGodMode(true);
    setConfirming(false);
  }

  return (
    <>
      <Button
        variant={godMode ? 'destructive' : 'outline'}
        size="sm"
        data-testid="god-mode-toggle"
        onClick={handleClick}
      >
        {godMode ? t('god_mode.on') : t('god_mode.off')}
      </Button>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent data-testid="god-mode-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t('god_mode.confirm_dialog.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('god_mode.confirm_dialog.body')}
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="god-mode-cancel-button"
              onClick={() => setConfirming(false)}
            >
              {t('god_mode.confirm_dialog.cancel')}
            </Button>
            <Button
              variant="destructive"
              data-testid="god-mode-confirm-button"
              onClick={handleConfirm}
            >
              {t('god_mode.confirm_dialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
